import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter } from '@/components/PublicFooter';
import { HoneypotFields, useHoneypot } from '@/components/Honeypot';
import { PasswordInput } from '@/components/PasswordInput';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';
import { Mail, Sparkles } from 'lucide-react';

// Registration (Feb 2026): email + password only. Username was removed —
// it added no value and created another failure surface. Email is the
// unique account identifier. The Super Admin login (/admin/login) keeps its
// separate username + password + PIN flow untouched.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const honeypot = useHoneypot();

  const showError = (msg) => {
    setInlineError(msg);
    toast.error(msg);
  };

  const submit = async (e) => {
    e.preventDefault();
    setInlineError('');
    const emailTrim = email.trim().toLowerCase();
    if (!EMAIL_RE.test(emailTrim)) {
      showError('Please enter a valid email address.');
      return;
    }
    if (password.length < 8) {
      showError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      const result = await register(emailTrim, password, honeypot.getValues());
      if (!result) {
        showError('Registration could not be completed. Please try again.');
        return;
      }
      toast.success('Your 3-day free trial has started.');
      navigate('/seek');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let message;
      if (Array.isArray(detail)) {
        message = detail[0]?.msg || 'Invalid registration details.';
      } else if (typeof detail === 'string' && detail) {
        message = detail;
      } else if (status === 409) {
        message = 'Email already registered.';
      } else if (status === 422 || status === 400) {
        message = 'Invalid email or password.';
      } else if (!err?.response) {
        message = 'Network error. Please try again.';
      } else {
        message = 'Server error. Please try again in a moment.';
      }
      showError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <ZitaLogo size={44} />
          <ZitaWordmark className="text-3xl" />
        </Link>
        <form onSubmit={submit} className="zita-card p-8 w-full max-w-md space-y-4" data-testid="register-form">
          <h1 className="font-serif text-3xl text-zita-espresso">Create your Zita account</h1>
          <div className="flex items-start gap-2 text-sm text-zita-cocoa bg-zita-sand/60 rounded-lg p-3">
            <Sparkles size={16} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">3-day free trial · no credit card required.</div>
              <div className="text-xs text-zita-mocha mt-0.5">You&apos;ll be asked to subscribe when the trial ends.</div>
            </div>
          </div>

          <HoneypotFields inputRef={honeypot.websiteRef} />

          {inlineError && (
            <div
              data-testid="register-error"
              role="alert"
              aria-live="polite"
              className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {inlineError}
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1" htmlFor="reg-email">
              Email
            </label>
            <div className="relative">
              <input
                id="reg-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.registerEmail}
                autoComplete="email"
                className="zita-input w-full pl-10 pr-4 py-3 text-sm"
              />
              <Mail size={16} className="absolute inset-y-0 my-auto left-3 text-zita-mocha pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1" htmlFor="reg-password">
              Password
            </label>
            <PasswordInput
              id="reg-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testId={AUTH.registerPassword}
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid={AUTH.registerSubmit}
            className="zita-btn-primary w-full"
          >
            {busy ? 'Creating account…' : 'Create account & start 3-day trial'}
          </button>
          <div className="text-sm text-zita-mocha text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-zita-cocoa hover:underline">Sign in</Link>
          </div>
          <div className="text-[11px] text-zita-muted text-center leading-relaxed">
            By creating an account you agree to our{' '}
            <Link to="/terms" className="hover:text-zita-cocoa underline">Terms and Conditions</Link>{' '}and{' '}
            <Link to="/privacy" className="hover:text-zita-cocoa underline">Privacy Policy</Link>.
          </div>
        </form>
      </main>
      <PublicFooter />
    </div>
  );
};

export default Register;
